import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type PointerEvent as ReactPointerEvent, type RefObject, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import * as api from "./api";
import { dayFromSearch, dayTitle, shiftDay } from "./driverRoutes";
import { accountName, forecastWords, levelDot } from "./fill";
import { type MapInfo, MapInfoBox } from "./MapInfoBox";
import { MapFullscreenButton } from "./MapFullscreen";
import {
  axisTop,
  BAND_COUNT,
  bandAtX,
  bandLabel,
  bandText,
  barColour,
  byFill,
  CHART_WIDTH,
  chartWidth,
  dayOptions,
  fillColumns,
  type FillRange,
  fromOptions,
  inRange,
  looseTanks,
  nextVisit,
  pctText,
  rangeLabel,
  readRange,
  rings,
  routeRows,
  selectedText,
  selectTanks,
  snapRange,
  sourceText,
  summaryText,
  TABLE_LIMIT,
  toOptions,
  writeRange,
} from "./fillView";
import { go, useTitle } from "./hooks";
import { FillBandKeys, LevelDot } from "./LevelDot";
import { ConeBar, useMarkerCone } from "./ConeCard";
import { TabHead } from "./Menu";
import { loadMaps, type GoogleMap, type Maps, type Marker, type Polyline, watchMapErrors } from "./maps";
import { isStraight, lineStyle, routePath, straightLegend } from "./polyline";
import { truckColour } from "./routePlan";
import { SERVER_PROBLEM } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { fillKey } from "./store/pageViews";
import { useView, ViewFreshness } from "./store/useView";
import { Skeleton } from "./Skeleton";

type Load = api.FillDay | "hidden" | "session" | "server" | "network" | { refused: string } | undefined;

export function fillFailure(e: unknown): Exclude<Load, api.FillDay | undefined> {
  const code = e instanceof api.ApiError ? e.status : 0;
  if (code === 422) return { refused: e instanceof Error ? e.message : "Choose another day." };
  return code === 404 ? "hidden" : code === 401 ? "session" : code >= 500 ? "server" : "network";
}
const DARK = "#0f1216";
const DEPOT_COLOUR = "#e8eaed";

/** Fewer than three points: a closer zoom shows no road and no town name. */
const FEW_POINTS = 3;
const FEW_POINTS_MAX_ZOOM = 11;

function fitFew(view: GoogleMap, bounds: unknown, points: number) {
  view.fitBounds(bounds, 48);
  if (points >= FEW_POINTS) return;
  const done = view.addListener("idle", () => {
    done.remove();
    if ((view.getZoom() ?? 0) > FEW_POINTS_MAX_ZOOM) view.setZoom(FEW_POINTS_MAX_ZOOM);
  });
}

/** The routes of the day as lines in the truck colours, each container as a level dot (U-22); a range outlines its containers. */
function FillMap({ base, user, found, range }: { base: string; user: User; found: api.FillDay; range: FillRange | null }) {
  const element = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const opener = useRef<Marker | null>(null);
  const [open, setOpen] = useState<MapInfo | null>(null);
  const cone = useMarkerCone();
  const map = useRef<GoogleMap | null>(null);
  const drawn = useRef<{ markers: Marker[]; lines: Polyline[] }>({ markers: [], lines: [] });
  const fitted = useRef<api.FillDay | null>(null);
  const [maps, setMaps] = useState<Maps | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const straightNote = straightLegend(found.routes.filter((route) => isStraight(route.shape)).map((route) => route.truck));
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(loadMaps)
      .then((loaded) => {
        if (!live) return;
        setMaps(loaded);
        setState("ready");
      })
      .catch(() => live && setState("failed"));
    return () => {
      live = false;
    };
  }, [user]);
  useEffect(() => {
    if (!maps || !element.current) return;
    const shown =
      map.current ??
      new maps.maps.Map(element.current, {
        center: { lat: 39.8, lng: -98.6 },
        zoom: 4,
        mapId: maps.mapId,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
      });
    map.current = shown;
    for (const marker of drawn.current.markers) marker.map = null;
    for (const line of drawn.current.lines) line.setMap(null);
    const markers: Marker[] = [];
    const lines: Polyline[] = [];
    const bounds = new maps.maps.LatLngBounds();
    const place = (position: api.LatLng, title: string, content: HTMLElement, zIndex: number, info: MapInfo) => {
      const marker = new maps.marker.AdvancedMarkerElement({ map: shown, position, title, content, zIndex, gmpClickable: true });
      marker.addListener("click", () => {
        opener.current = marker;
        setOpen(info);
      });
      cone.watch(content, info.accountId, info.name, { words: info.fillText });
      markers.push(marker);
    };
    const look = (fill: number | null) => {
      const picked = range !== null && fill !== null && inRange(range, fill);
      return { picked, faded: range !== null && !picked };
    };
    const depots = new Map(found.depots.flatMap((d) => (d.lat !== null && d.lng !== null ? [[d.id, { lat: d.lat, lng: d.lng, name: d.name }] as const] : [])));
    for (const depot of depots.values()) {
      const pin = new maps.marker.PinElement({ glyph: "D", background: DEPOT_COLOUR, borderColor: DARK, glyphColor: DARK });
      markers.push(new maps.marker.AdvancedMarkerElement({ map: shown, position: depot, title: depot.name, content: pin.element, zIndex: 3000 }));
    }
    let picked = 0;
    let fitPoints = 0;
    const names = new Map(found.tanks.map((tank) => [tank.account_id, tank.name]));
    for (const tank of looseTanks(found.tanks, found.high_share, range)) {
      const point = { lat: tank.lat as number, lng: tank.lng as number };
      const loose = tank.fill >= found.high_share;
      const name = accountName(tank.number, tank.name);
      const title = [name, "On no route", forecastWords(tank.float, tank.fill)].join(" · ");
      const dot = look(tank.fill);
      picked += dot.picked ? 1 : 0;
      const visit = nextVisit(tank, found.day);
      const info = { id: tank.account_id, accountId: tank.account_id, title: tank.number, name, share: tank.fill, fillText: tank.float ? forecastWords(tank.float, tank.fill) : undefined, lines: ["On no route", visit && `Next visit ${visit}`], href: `${base}/accounts/${tank.account_id}` };
      place(point, title, levelDot({ share: tank.fill, loose, ...dot }), dot.picked ? 2500 : 500, info);
      if (loose) {
        bounds.extend(point);
        fitPoints += 1;
      }
    }
    for (const route of found.routes) {
      const colour = truckColour(route.colour);
      const depot = route.depot_id ? (depots.get(route.depot_id) ?? null) : null;
      const path: api.LatLng[] = depot ? [depot] : [];
      route.stops.forEach((stop, at) => {
        if (stop.lat === null || stop.lng === null) return;
        const point = { lat: stop.lat, lng: stop.lng };
        path.push(point);
        const name = accountName(stop.number, stop.name ?? names.get(stop.account_id));
        const title = [`Stop ${at + 1}`, name, `Truck ${route.truck}`, forecastWords(stop.float, stop.fill)].join(" · ");
        const dot = look(stop.fill);
        picked += dot.picked ? 1 : 0;
        const info = { id: `${route.truck}-${at}`, accountId: stop.account_id, title: `Stop ${at + 1} · Truck ${route.truck}`, name, share: stop.fill, fillText: stop.float ? forecastWords(stop.float, stop.fill) : undefined, href: `${base}/accounts/${stop.account_id}` };
        place(point, title, levelDot({ share: stop.fill, number: at + 1, ...dot }), (dot.picked ? 2000 : 1000) + at, info);
        bounds.extend(point);
        fitPoints += 1;
      });
      if (depot) path.push(depot);
      const line = new maps.maps.Polyline({ ...lineStyle(isStraight(route.shape), colour, range ? 0.45 : 0.9, 3), clickable: false });
      line.setPath(routePath(route.shape, path));
      line.setMap(shown);
      lines.push(line);
    }
    drawn.current = { markers, lines };
    console.info(`fill map: ${markers.length} markers and ${lines.length} lines${range ? `, ${picked} in the range ${rangeLabel(range)}` : ""}`);
    if (fitted.current === found) return;
    fitted.current = found;
    if (markers.length > depots.size) fitFew(shown, bounds, fitPoints);
  }, [maps, found, range]);
  useEffect(() => {
    const target = element.current;
    if (state !== "ready" || !target) return;
    return watchMapErrors(target, () => setState("failed"));
  }, [state]);
  useEffect(() => setOpen(null), [found]);
  useEffect(
    () => () => {
      for (const marker of drawn.current.markers) marker.map = null;
      for (const line of drawn.current.lines) line.setMap(null);
    },
    [],
  );
  return (
    <div ref={wrap} className="pin-map-wrap fill-map-wrap">
      <div ref={element} className="pin-map-canvas fill-map" data-testid="fill-map" data-state={state} data-range={range ? rangeLabel(range) : ""} role="region" aria-label="Map of the routes and the estimated fill of the containers" />
      {state === "ready" && <MapFullscreenButton wrap={wrap} testId="fill-map-fullscreen" />}
      {state === "loading" && <Skeleton shape="map" overlay label="Loading the map" />}
      {state !== "failed" && straightNote && (
        <p className="sub location-map-note map-straight-note" data-testid="fill-map-straight">
          <span className="map-dash" aria-hidden="true" /> {straightNote}
        </p>
      )}
      {state === "failed" && (
        <p className="location-map-note map-warning" data-testid="fill-map-failed">
          The map cannot load. The chart and the truck list show the estimates.
        </p>
      )}
      {cone.card}
      {open && (
        <MapInfoBox
          testId="fill-map-box"
          info={open}
          onClose={() => {
            setOpen(null);
            (opener.current as unknown as HTMLElement | null)?.focus?.();
          }}
        />
      )}
    </div>
  );
}

const CHART = { height: 200, left: 36, right: 8, top: 10, bottom: 34 };

/** The view box width that follows the CSS width of the figure (see chartWidth). */
function useChartWidth(figure: RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(CHART_WIDTH);
  useLayoutEffect(() => {
    const target = figure.current;
    if (!target) return;
    const measure = () =>
      setWidth((was) => {
        const next = chartWidth(target.getBoundingClientRect().width);
        if (next !== was) console.info(`fill chart: view box ${next} px wide`);
        return next;
      });
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const watch = new ResizeObserver(measure);
    watch.observe(target);
    return () => watch.disconnect();
  }, [figure]);
  return width;
}

/** 21 bars of the containers by fill band. A drag or a click selects a range of bars; a hover gives the count of a bar. */
function FillChart({
  bands,
  summary,
  range,
  onRange,
  fromList,
}: {
  bands: number[];
  summary: string;
  range: FillRange | null;
  onRange: (next: FillRange | null) => void;
  fromList: RefObject<HTMLSelectElement>;
}) {
  const figure = useRef<HTMLElement>(null);
  const width = useChartWidth(figure);
  const [picked, setPicked] = useState<number | null>(null);
  const [drag, setDrag] = useState<{ anchor: number; range: FillRange } | null>(null);
  const top = axisTop(bands);
  const inner = width - CHART.left - CHART.right;
  const step = inner / BAND_COUNT;
  const base = CHART.height - CHART.bottom;
  const y = (count: number) => base - (count / top) * (base - CHART.top);
  const shown = drag?.range ?? range;
  const bandAt = (e: ReactPointerEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    return bandAtX(((e.clientX - box.left) / Math.max(box.width, 1)) * width, CHART.left, step);
  };
  const down = (e: ReactPointerEvent<SVGSVGElement>) => {
    if (e.button !== 0) return;
    const band = bandAt(e);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDrag({ anchor: band, range: snapRange(band, band) });
  };
  const move = (e: ReactPointerEvent<SVGSVGElement>) => {
    const band = bandAt(e);
    setPicked(band);
    if (drag) setDrag({ anchor: drag.anchor, range: snapRange(drag.anchor, band) });
  };
  const up = () => {
    if (!drag) return;
    setDrag(null);
    onRange(drag.range);
  };
  const edge = (band: number) => CHART.left + band * step;
  const gap = step > 20 ? 1.5 : 0.75;
  return (
    <figure ref={figure} className="fill-chart" data-testid="fill-chart">
      <figcaption>
        <h2 id="fill-chart-title">Containers by estimated fill</h2>
        <p data-testid="fill-summary">{summary}</p>
      </figcaption>
      <svg
        viewBox={`0 0 ${width} ${CHART.height}`}
        role="img"
        aria-labelledby="fill-chart-title"
        aria-describedby="fill-chart-help fill-chart-table"
        data-testid="fill-chart-svg"
        data-range={shown ? `${shown.lo}-${shown.hi}` : ""}
        className={drag ? "dragging" : undefined}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={() => setDrag(null)}
        onPointerLeave={() => setPicked(null)}
      >
        {[0, top / 2, top].map((tick) => (
          <g key={tick}>
            <line className="chart-grid" x1={CHART.left} x2={width - CHART.right} y1={y(tick)} y2={y(tick)} />
            <text className="chart-axis" x={CHART.left - 4} y={y(tick) + 4} textAnchor="end">
              {tick}
            </text>
          </g>
        ))}
        {shown && <rect className="fill-brush" x={edge(shown.lo)} y={CHART.top - 4} width={edge(shown.hi + 1) - edge(shown.lo)} height={base - CHART.top + 8} data-testid="fill-brush" />}
        {bands.map((count, band) => {
          const out = shown !== null && (band < shown.lo || band > shown.hi);
          return (
            <g key={band} data-testid="fill-bar" data-band={band} data-count={count} data-out={out ? "1" : undefined} className={`fill-bar${picked === band ? " picked" : ""}${out ? " out" : ""}`}>
              <title>{bandText(band, count)}</title>
              <rect className="fill-bar-hit" x={edge(band)} y={CHART.top} width={step} height={base - CHART.top} />
              <rect className="fill-bar-value" style={{ fill: barColour(band) }} x={edge(band) + gap} y={y(count)} width={step - 2 * gap} height={Math.max(base - y(count), count ? 1 : 0)} />
            </g>
          );
        })}
        {shown &&
          [shown.lo, shown.hi + 1].map((band, at) => <rect key={at} className="fill-brush-handle" x={edge(band) - 3} y={(CHART.top + base) / 2 - 12} width={6} height={24} rx={2} />)}
        {[0, 5, 10, 15, 20].map((band) => (
          <text key={band} className="chart-axis" x={CHART.left + band * step + step / 2} y={base + 16} textAnchor="middle">
            {band === 20 ? "100+" : band * 5}
          </text>
        ))}
        <text className="chart-axis" x={CHART.left + inner / 2} y={CHART.height - 2} textAnchor="middle">
          Estimated fill, % of the container (5% bands)
        </text>
      </svg>
      <p className="sub fill-band-detail" aria-live="polite" data-testid="fill-band-detail">
        {picked === null ? "Drag across the bars to select a fill range, or tap a bar." : bandText(picked, bands[picked] ?? 0)}
      </p>
      <p className="visually-hidden" id="fill-chart-help">
        Use the From and To lists below the chart to select a fill range with the keyboard.
      </p>
      <RangePick range={range} onRange={onRange} fromList={fromList} />
      <table className="visually-hidden" id="fill-chart-table">
        <caption>Containers in each fill band</caption>
        <tbody>
          {bands.map((count, band) => (
            <tr key={band}>
              <th scope="row">{bandLabel(band)}</th>
              <td>{count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/** The keyboard path to a range: two lists of the 5% edges. */
function RangePick({ range, onRange, fromList }: { range: FillRange | null; onRange: (next: FillRange | null) => void; fromList: RefObject<HTMLSelectElement> }) {
  const lo = range?.lo ?? 0;
  const hi = range?.hi ?? BAND_COUNT - 1;
  return (
    <div className="fill-range-pick" role="group" aria-label="Fill range">
      <label>
        From{" "}
        <select ref={fromList} data-testid="fill-range-from" value={range ? lo : ""} onChange={(e) => e.target.value !== "" && onRange(snapRange(Number(e.target.value), Math.max(Number(e.target.value), hi)))}>
          {!range && <option value="">Any</option>}
          {fromOptions().map((option) => (
            <option key={option.band} value={option.band}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        to{" "}
        <select data-testid="fill-range-to" value={range ? hi : ""} onChange={(e) => e.target.value !== "" && onRange(snapRange(Math.min(Number(e.target.value), lo), Number(e.target.value)))}>
          {!range && <option value="">Any</option>}
          {toOptions().map((option) => (
            <option key={option.band} value={option.band}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/** The containers in the range: a count, a clear link and a sortable table (U-5, U-6). */
function Selection({ base, found, range, onClear }: { base: string; found: api.FillDay; range: FillRange; onClear: () => void }) {
  const columns = useMemo(() => fillColumns(found.day), [found.day]);
  const [sort, setSort] = useSort("sort", columns);
  const chosen = useMemo(() => selectTanks(found.tanks, range), [found.tanks, range]);
  const rows = useMemo(() => (sort ? sortRows(chosen, columns, sort) : byFill(chosen)), [chosen, columns, sort]);
  const shown = rows.slice(0, TABLE_LIMIT);
  return (
    <section className="fill-selection" aria-labelledby="fill-selected" data-testid="fill-selection">
      <div className="fill-selection-head">
        <h3 id="fill-selected" data-testid="fill-selected" data-count={chosen.length}>
          {selectedText(range, chosen.length)}
        </h3>
        <button type="button" className="link" data-testid="fill-clear" onClick={onClear}>
          Clear the selection
        </button>
      </div>
      {chosen.length === 0 ? (
        <p data-testid="fill-selection-none">No container is in this range on this day.</p>
      ) : (
        <>
          <SortSelect id="fill-select" columns={columns} sort={sort} onSort={setSort} fallback="Fill, fullest first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Containers in the range">
            <table className="list fill-tanks" data-testid="fill-tank-table" data-cols="fill-select">
              <thead>
                <tr>
                  {columns.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((tank) => {
                  const next = nextVisit(tank, found.day);
                  return (
                    <tr key={tank.account_id} data-testid="fill-tank" data-number={tank.number}>
                      <th scope="row">
                        <a href={`${base}/accounts/${tank.account_id}`} onClick={go}>
                          {tank.number}
                        </a>
                      </th>
                      <td data-label="Name">{tank.name ?? "—"}</td>
                      <td className="num" data-label="Fill">
                        <span className="fill-cell">
                          <ConeBar share={tank.fill} accountId={tank.account_id} label={accountName(tank.number, tank.name)} words={tank.float ? forecastWords(tank.float, tank.fill) : undefined} />
                          {tank.float ? <TwoParts text={forecastWords(tank.float, tank.fill)} /> : pctText(tank.fill)}
                        </span>
                      </td>
                      <td data-label="Truck">{tank.truck ?? "—"}</td>
                      <td data-label="Next visit">{next ? dayTitle(next, found.today) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length > shown.length && (
            <p className="sub" data-testid="fill-table-limit">
              The table shows {shown.length} of {rows.length.toLocaleString("en-US")} containers. Select a smaller range to see the others.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** "Below the float (75%)", then the rest on a line under it. */
function TwoParts({ text }: { text: string }) {
  const [first, ...rest] = text.split(" · ");
  return (
    <>
      {first}
      {rest.length > 0 && <span className="sub below">{rest.join(" · ")}</span>}
    </>
  );
}

function RouteTable({ found }: { found: api.FillDay }) {
  const share = Math.round(found.high_share * 100);
  return (
    <div className="table-scroll" tabIndex={0} role="region" aria-label="Estimated fill on each truck">
      <table className="list fill-routes" data-testid="fill-route-table">
        <thead>
          <tr>
            <th scope="col">Truck</th>
            <th scope="col" className="num">
              Stops
            </th>
            <th scope="col" className="num">
              <span aria-hidden="true">≥ {share}%</span>
              <span className="visually-hidden">At or over {share}%</span>
            </th>
            <th scope="col" className="num">
              Mean fill
            </th>
          </tr>
        </thead>
        <tbody>
          {routeRows(found.routes, found.high_share).map((row) => (
            <tr key={row.truck} data-testid="fill-route" data-truck={row.truck} data-proposed={row.proposed ? "1" : undefined}>
              <th scope="row">
                <span className="route-stop-number" style={{ background: truckColour(row.colour) }} aria-hidden="true">
                  {row.truck}
                </span>{" "}
                <span className="visually-hidden">Truck </span>
                {row.truck}
                {found.source === "mixed" && row.proposed && <span className="hint"> (proposed)</span>}
              </th>
              <td className="num" data-label="Stops">
                {row.stops}
              </td>
              <td className="num" data-label={`At or over ${share}%`}>
                {row.high}
              </td>
              <td className="num" data-label="Mean fill">
                {pctText(row.mean)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Legend({ share }: { share: number }) {
  return (
    <ul className="pin-legend" data-testid="fill-legend">
      <FillBandKeys />
      <li>
        <LevelDot share={null} small /> No estimate
      </li>
      <li>
        <LevelDot share={0.95} loose small /> On no route, at or over {Math.round(share * 100)}%
      </li>
      <li>Line: the route of a truck, in its colour</li>
    </ul>
  );
}

/** The saved routes of one day on a map, and all the containers by estimated fill, from today to 14 days after today. */
export function FillViewPage({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [day, setDayState] = useState<string | undefined>(() => dayFromSearch(location.search));
  const [range, setRangeState] = useState<FillRange | null>(() => readRange(location.search));
  const setRange = (next: FillRange | null) => {
    setRangeState(next);
    history.replaceState(history.state, "", `${location.pathname}${writeRange(location.search, next)}${location.hash}`);
    console.info(`fill view: ${next ? `range ${rangeLabel(next)}` : "no range"}`);
  };
  const fromList = useRef<HTMLSelectElement>(null);
  const clearRange = () => {
    setRange(null);
    fromList.current?.focus();
  };
  const view = useView(canView === true ? fillKey(day) : null, async (signal) => {
    const started = performance.now();
    const next = await api.fillDay(await user.getIdToken(), day, signal);
    console.info(`fill view: ${next.tanks.length} containers and ${next.routes.length} routes of ${next.day} in ${Math.round(performance.now() - started)} ms`);
    return next;
  });
  const failed = view.error && !view.checking ? fillFailure(view.error) : undefined;
  const found: Load = failed === "hidden" || typeof failed === "object" ? failed : (view.data ?? failed);
  const last = useRef<api.FillDay | null>(null);
  if (view.data) last.current = view.data;
  useTitle(`Containers · Fill forecast · ${org.name}`);
  const setDay = (next: string) => {
    setDayState(next);
    const params = new URLSearchParams(location.search);
    params.set("day", next);
    history.replaceState(history.state, "", `${location.pathname}?${params.toString()}${location.hash}`);
  };
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="fill-hidden">
        <TabHead />
        <p>Your role does not show the fill forecast.</p>
      </main>
    );
  }
  const shown = found && typeof found === "object" && "tanks" in found ? found : undefined;
  const frame = shown ?? last.current;
  const options = frame ? dayOptions(frame.today, frame.last_day) : [];
  const current = shown?.day ?? day ?? frame?.today;
  return (
    <main className="issues-page fill-page wide" data-testid="fill-page" data-day={shown?.day ?? ""}>
      <TabHead />
      {frame && current && (
        <div className="tabs" role="group" aria-label="Day">
          <button type="button" data-testid="fill-day-before" disabled={current <= frame.today} onClick={() => setDay(shiftDay(current, -1))}>
            Day before
          </button>
          <button type="button" data-testid="fill-day-today" aria-pressed={current === frame.today} onClick={() => setDay(frame.today)}>
            Today
          </button>
          <button type="button" data-testid="fill-day-after" disabled={current >= frame.last_day} onClick={() => setDay(shiftDay(current, 1))}>
            Day after
          </button>
          <label className="day-pick">
            <span className="visually-hidden">Day</span>
            <select data-testid="fill-day-select" value={options.some((o) => o.day === current) ? current : ""} onChange={(e) => e.target.value && setDay(e.target.value)}>
              {!options.some((o) => o.day === current) && <option value="">Choose a day…</option>}
              {options.map((option) => (
                <option key={option.day} value={option.day}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}
      <p className="sub" data-testid="fill-horizon">
        The view goes to 14 days ahead. The pickups of each day change the later estimates, so a far day is less sure.
      </p>
      {view.data && found === view.data && <ViewFreshness view={view} timeZone={org.time_zone} auth={auth} />}
      {found === undefined ? (
        <Skeleton shape="map" />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => void signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error" data-testid="fill-failed">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={view.reload}>
            Try again
          </button>
        </p>
      ) : "refused" in found ? (
        <p role="alert" className="error" data-testid="fill-refused">
          {found.refused}{" "}
          <button type="button" data-testid="fill-show-today" onClick={() => (frame ? setDay(frame.today) : setDayState(undefined))}>
            Show today
          </button>
        </p>
      ) : (
        <>
          <h2 data-testid="fill-day">{dayTitle(found.day, found.today)}</h2>
          <p data-testid="fill-source" data-source={found.source}>
            {sourceText(found)}
          </p>
          {found.tanks.length === 0 && (
            <p data-testid="fill-estimates-none">No container has a fill estimate yet. The nightly job fits the estimates from the pickups and the sensors.</p>
          )}
          <div className="fill-layout">
            <section className="fill-map-side" aria-label="Map">
              <Legend share={found.high_share} />
              <FillMap base={base} user={user} found={found} range={range} />
              {(() => {
                const ringed = rings(found.tanks, found.high_share);
                return ringed.total > ringed.shown.length ? (
                  <p className="sub" data-testid="fill-rings-note">
                    The map shows the {ringed.shown.length} fullest of {ringed.total.toLocaleString("en-US")} containers on no route at or over {Math.round(found.high_share * 100)}%.
                  </p>
                ) : null;
              })()}
            </section>
            <section className="fill-chart-side" aria-label="Chart and trucks">
              <FillChart bands={found.bands} summary={summaryText(found)} range={range} onRange={setRange} fromList={fromList} />
              {range && <Selection base={base} found={found} range={range} onClear={clearRange} />}
              <h2>Trucks</h2>
              {found.routes.length === 0 ? <p data-testid="fill-routes-none">No route on this day.</p> : <RouteTable found={found} />}
            </section>
          </div>
        </>
      )}
    </main>
  );
}
